import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, Drawer, EmptyState, LoadingSpinner, confirmAlert } from 'components/common';
import { LuHistory, LuRotateCcw, LuSave, LuUndo2 } from 'react-icons/lu';
import { toast } from 'react-toastify';
import DateTimeToText from 'helpers/dateTimetoText';
import {
  getPifRestorePoints,
  getPifRestorePoint,
  restorePif,
} from 'redux/crm/project/pifSlice';
import { escapeHtml } from 'helpers/utils';
import { StruckText } from './DiffWords';

// Point-in-time recovery for a PIF.
//
// Every save already records the whole gds/areas jsonb, before and after, in the
// audit trail — so the history IS the set of restore points and there is nothing
// extra to store. Restoring overwrites, so a point is always previewed as a diff
// against the PIF as it stands before anything is written.

const KIND_BADGE = {
  added: 'tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200',
  removed: 'tw-bg-rose-50 tw-text-rose-700 tw-border-rose-200',
  changed: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
};

const absoluteTime = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value || '') : parsed.toLocaleString();
};

// The stored comments are written for the history tab and carry internal ids
// ("restored from history entry #1173660"), which mean nothing to a salesperson.
const describePoint = (point) => {
  const restored = /restor/i.test(point.comment || '');
  return {
    restored,
    label: restored ? 'Restored from an earlier version' : 'Saved',
    Icon: restored ? LuUndo2 : LuSave,
  };
};

const ChangeRow = ({ change }) => (
  <div className="tw-py-2 tw-border-b tw-border-slate-100 last:tw-border-0">
    <div className="tw-flex tw-items-center tw-gap-2">
      <span className={`tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-border ${KIND_BADGE[change.kind] || ''}`}>
        {change.kind}
      </span>
      <span className="tw-text-xs tw-font-medium tw-text-slate-700">{change.label || change.path}</span>
    </div>
    <div className="tw-mt-1 tw-text-xs tw-flex tw-flex-wrap tw-gap-x-2 tw-gap-y-0.5 tw-items-baseline">
      <span className="tw-text-[10px] tw-uppercase tw-text-slate-400 tw-tracking-wide">now</span>
      <StruckText value={change.new} className="tw-text-slate-400 tw-line-through" max={120} />
      <span className="tw-text-[10px] tw-uppercase tw-text-slate-400 tw-tracking-wide">after restore</span>
      <StruckText value={change.old} className="tw-text-slate-800 tw-font-medium" max={120} />
    </div>
  </div>
);
ChangeRow.propTypes = { change: PropTypes.object.isRequired };

const RestorePointsDrawer = ({ show, onHide, pif, onRestored }) => {
  const dispatch = useDispatch();

  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const projectId = pif?.project?.id;
  const buildingId = pif?.building?.id;
  const adaptationId = pif?.adaptation?.id;
  const pifId = pif?.id;
  const ids = useMemo(
    () => ({ projectId, buildingId, adaptationId, pifId }),
    [projectId, buildingId, adaptationId, pifId],
  );

  const loadPoints = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(getPifRestorePoints(ids)).unwrap();
      setPoints(data);
    } catch (error) {
      toast.error(error?.message || 'Failed to load restore points.');
    } finally {
      setLoading(false);
    }
  }, [dispatch, ids]);

  useEffect(() => {
    if (!show || !pif?.id) return;
    setSelected(null);
    setPreview(null);
    loadPoints();
  }, [show, pif?.id, loadPoints]);

  const choose = async (point) => {
    setSelected(point);
    setPreviewing(true);
    setPreview(null);

    try {
      const data = await dispatch(getPifRestorePoint({ ...ids, auditId: point.id })).unwrap();
      setPreview(data);
    } catch (error) {
      toast.error(error?.message || 'Failed to preview this restore point.');
    } finally {
      setPreviewing(false);
    }
  };

  const changes = preview?.changes || [];
  const identical = Boolean(preview) && changes.length === 0;

  const apply = () => {
    if (!selected) return;

    confirmAlert({
      title: 'Restore this PIF?',
      // confirmAlert renders the message as HTML.
      message:
        `This PIF will be overwritten with its content from <strong>${escapeHtml(absoluteTime(selected.created_at))}</strong>, ` +
        `changing <strong>${changes.length}</strong> field${changes.length === 1 ? '' : 's'}. ` +
        'The restore is itself recorded, so it can be undone from this same list.',
      onConfirm: async () => {
        setRestoring(true);
        try {
          await dispatch(restorePif({ ...ids, auditId: selected.id })).unwrap();
          if (onRestored) onRestored();
          onHide();
        } catch (error) {
          toast.error(error?.message || 'Failed to restore the PIF.');
        } finally {
          setRestoring(false);
        }
      },
    });
  };

  const footerHint = () => {
    if (!selected) return 'Pick a point to see what it would change.';
    if (previewing) return 'Comparing...';
    if (identical) return 'Identical to the current PIF.';
    return `${changes.length} field${changes.length === 1 ? '' : 's'} would change.`;
  };

  return (
    <Drawer
      show={show}
      onHide={onHide}
      width="680px"
      title={
        <span className="tw-flex tw-items-center tw-gap-2">
          <LuHistory size={16} /> Restore points
        </span>
      }
      footer={
        <>
          <span className="tw-mr-auto tw-text-xs tw-text-slate-400">{footerHint()}</span>
          <Button variant="ghost" size="sm" onClick={onHide} disabled={restoring}>
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={apply}
            disabled={!selected || previewing || identical || restoring}
            leftIcon={LuRotateCcw}
          >
            {restoring ? 'Restoring...' : 'Restore this version'}
          </Button>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-h-full tw-min-h-0">
        {loading && <LoadingSpinner message="Loading history..." />}

        {!loading && points.length === 0 && (
          <EmptyState
            icon={LuHistory}
            title="No restore points yet"
            description="A restore point is written every time this PIF is saved."
          />
        )}

        {!loading && points.length > 0 && (
          <>
            <p className="tw-text-xs tw-text-slate-400 tw-mb-2 tw-mt-0">
              Every save of this PIF leaves a restore point. Nothing is written until you confirm.
            </p>

            <div className="tw-flex tw-flex-col tw-gap-1 tw-overflow-y-auto tw-pr-1"
                 style={{ maxHeight: '38%' }}>
              {points.map((point, index) => {
                const { label, Icon, restored } = describePoint(point);
                const isSelected = selected?.id === point.id;

                return (
                  <button
                    key={point.id}
                    type="button"
                    onClick={() => choose(point)}
                    title={absoluteTime(point.created_at)}
                    className={`tw-text-left tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-2 tw-rounded tw-border tw-border-l-[3px] tw-transition-colors ${
                      isSelected
                        ? 'tw-border-primary-200 tw-border-l-primary-500 tw-bg-primary-50'
                        : 'tw-border-slate-200 tw-border-l-transparent hover:tw-bg-slate-50'
                    }`}
                  >
                    <Icon
                      size={13}
                      className={`tw-mt-0.5 tw-flex-shrink-0 ${restored ? 'tw-text-amber-500' : 'tw-text-slate-400'}`}
                    />
                    <div className="tw-min-w-0 tw-flex-1">
                      <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
                        <span className="tw-text-xs tw-font-medium tw-text-slate-700">
                          <DateTimeToText datetime={point.created_at} />
                        </span>
                        <span className="tw-text-xs tw-text-slate-400">· {point.created_by || 'System'}</span>
                        {index === 0 && (
                          <span className="tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-border tw-border-slate-200 tw-text-slate-500">
                            latest
                          </span>
                        )}
                      </div>
                      <div className="tw-text-xs tw-text-slate-400">{label}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-slate-200 tw-flex-1 tw-min-h-0 tw-flex tw-flex-col">
              <div className="tw-text-xs tw-font-semibold tw-text-slate-600 tw-mb-2">
                What restoring would change
              </div>

              {!selected && (
                <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-slate-300">
                  Select a restore point above
                </div>
              )}

              {selected && previewing && <LoadingSpinner size="sm" message="Comparing..." />}

              {selected && !previewing && identical && (
                <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-slate-400">
                  This version is identical to the PIF as it stands now.
                </div>
              )}

              {selected && !previewing && changes.length > 0 && (
                <div className="tw-overflow-y-auto tw-pr-1 tw-flex-1 tw-min-h-0">
                  {changes.map((change) => (
                    <ChangeRow key={change.path} change={change} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

RestorePointsDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  pif: PropTypes.object,
  onRestored: PropTypes.func,
};

export default RestorePointsDrawer;
